// ============================================================================
// ecgOfflineQueue.ts — ОФЛАЙН-ОЧЕРЕДЬ ЭКГ (offline-first, durable).
//
// Два вида отложенной работы:
//   • captured — снимок СНЯТ/ВЫБРАН без связи, разбор ещё не выполнялся.
//     При восстановлении связи снимок уходит на сервер (EcgService.AnalyzeEcg),
//     результат сохраняется в cardio_studies; повтор разбора НЕ выполняется,
//     если разбор уже получен (stage='analyzed');
//   • analyzed — разбор получен, но сохранение не удалось (связь пропала между
//     загрузкой снимка и созданием записи) — сохраняем результат без повторного
//     разбора.
//
// Границы:
//   • разбор (EcgService.AnalyzeEcg) — СЕРВЕР платформы, офлайн невозможен:
//     снимок ждёт связи в очереди (durable IndexedDB), а не теряется;
//   • серверные ошибки (права/валидация/снимок не разобран) НЕ ретраятся
//     автоматически — элемент остаётся в очереди с текстом ошибки, человек
//     может повторить или отменить;
//   • отправка последовательная и single-flight, с отменой и прогрессом.
//
// SSoT данных — сервер; очередь — durable буфер записи (как chatQueue /
// chunkedUpload), привязана к базе (slug), переживает перезагрузку.
// ============================================================================

import { createLogger } from '@/lib/logger';
import { friendlyError, isTransportError, logErrorToJournal } from '@/lib/errors';
import { isEffectivelyOffline } from '@/lib/offlineFlag';
import { idbGet, idbSet } from '@/lib/storage/idb';
import { useCenterStore } from '@/store/centerStore';
import { analyzeEcgFile } from '@/lib/ecgAnalysis';
import {
    EcgSaveOfflineError,
    buildQualityLine,
    formatStudyName,
    persistEcgStudy,
    type EcgStudyFinding,
    type EcgStudyMeasurements,
    type SaveEcgStudyInput,
    type SaveEcgStudyResult,
} from '@/lib/ecgStudies';

const log = createLogger('EcgQueue');

/** captured — только снимок; analyzed — готовый разбор (ждёт сохранения). */
export type EcgQueueStage = 'captured' | 'analyzed';
/** Состояние отправки элемента (sending после перезапуска считается queued). */
export type EcgQueueState = 'queued' | 'sending' | 'error';

export interface EcgQueueItem {
    id: string;
    /** Момент съёмки/разбора (ISO) — имя разбора и подпись в очереди. */
    createdAt: string;
    ownerId: string;
    fileName: string;
    fileType: string;
    fileBytes: ArrayBuffer;
    stage: EcgQueueStage;
    state: EcgQueueState;
    /** Результат разбора (для stage='analyzed'). */
    studyStatus: 'processed' | 'retake';
    qualityLine: string;
    reason: string;
    pipelineVersion: string;
    measurements: EcgStudyMeasurements;
    findings: EcgStudyFinding[];
    /** Файл уже загружен, но запись не создана — повтор не грузит заново. */
    fileId: string;
    /** Резкость кадра, оценённая на устройстве до отправки (для строки качества). */
    captureSharpness: number | null;
    attempts: number;
    lastError: string;
}

/** Фаза отправки элемента (для индикатора прогресса в очереди). */
export type EcgQueuePhase = 'analyzing' | 'uploading' | 'saving';

export interface EcgQueueProgress {
    id: string;
    phase: EcgQueuePhase;
    /** 0..1 для загрузки снимка; null — неопределённый прогресс (разбор). */
    percent: number | null;
}

export interface EcgQueueFlushResult {
    saved: number;
    failed: number;
    pending: number;
    /** id отправленных элементов (UI понимает, что именно сохранилось). */
    savedIds: string[];
}

export interface QueueEcgCaptureInput {
    ownerId: string;
    file: File;
    /** Резкость кадра, оценённая на устройстве (может быть null). */
    sharpnessScore?: number | null;
    /** Причина (например, «нужно переснять») — заполняется после разбора. */
    reason?: string;
}

type Listener = () => void;
const listeners = new Set<Listener>();
const progressListeners = new Set<Listener>();

function emit(): void {
    for (const fn of listeners) {
        try {
            fn();
        } catch (e) {
            log.warn('queue listener failed', e);
        }
    }
}

function emitProgress(): void {
    for (const fn of progressListeners) {
        try {
            fn();
        } catch (e) {
            log.warn('progress listener failed', e);
        }
    }
}

/** Подписка на изменения очереди (UI обновляет счётчик без таймеров). */
export function onEcgQueueChange(fn: Listener): () => void {
    listeners.add(fn);
    return () => {
        listeners.delete(fn);
    };
}

/** Подписка на прогресс отправки (фаза + проценты загрузки снимка). */
export function onEcgQueueProgress(fn: Listener): () => void {
    progressListeners.add(fn);
    return () => {
        progressListeners.delete(fn);
    };
}

const progress = new Map<string, EcgQueueProgress>();

/** Текущий прогресс отправки (пусто — ничего не отправляется). */
export function getEcgQueueProgress(): EcgQueueProgress[] {
    return Array.from(progress.values());
}

function setProgress(id: string, phase: EcgQueuePhase, percent: number | null): void {
    const next = Math.max(0, Math.min(1, percent === null ? 0 : percent));
    const current = progress.get(id);
    if (current && current.phase === phase && current.percent === (percent === null ? null : next)) return;
    progress.set(id, { id, phase, percent: percent === null ? null : next });
    emitProgress();
}

function clearProgress(id: string): void {
    if (!progress.delete(id)) return;
    emitProgress();
}

function queueKey(): string {
    return `ecg_study_queue:${useCenterStore.getState().baseSlug || 'default'}`;
}

/** Сохранённый формат прежних версий (status = результат разбора). */
interface StoredEcgQueueItem extends Partial<EcgQueueItem> {
    status?: 'processed' | 'retake';
}

/**
 * normalizeItem — совместимость с элементами, сохранёнными прежними версиями:
 *   • status (processed/retake) → studyStatus;
 *   • отсутствующий stage → analyzed (раньше в очередь попадал готовый разбор);
 *   • state='sending' после перезапуска → queued (отправка прервалась);
 *   • прогресс/счётчики восстанавливаются без потери снимка.
 */
function normalizeItem(raw: StoredEcgQueueItem): EcgQueueItem {
    const stage: EcgQueueStage = raw.stage === 'captured' ? 'captured' : 'analyzed';
    const state: EcgQueueState = raw.state === 'error' ? 'error' : 'queued';
    const studyStatus: 'processed' | 'retake' =
        raw.studyStatus === 'retake' || raw.status === 'retake' ? 'retake' : 'processed';
    return {
        id: String(raw.id || crypto.randomUUID()),
        createdAt: String(raw.createdAt || new Date().toISOString()),
        ownerId: String(raw.ownerId || ''),
        fileName: String(raw.fileName || 'ecg.jpg'),
        fileType: String(raw.fileType || 'image/jpeg'),
        fileBytes: raw.fileBytes instanceof ArrayBuffer ? raw.fileBytes : new ArrayBuffer(0),
        stage,
        state,
        studyStatus,
        qualityLine: String(raw.qualityLine || ''),
        reason: String(raw.reason || ''),
        pipelineVersion: String(raw.pipelineVersion || ''),
        measurements: raw.measurements || {
            hrBpm: null,
            prMs: null,
            qrsMs: null,
            qtMs: null,
            qtcMs: null,
            axisDeg: null,
        },
        findings: Array.isArray(raw.findings) ? raw.findings : [],
        fileId: String(raw.fileId || ''),
        captureSharpness: typeof raw.captureSharpness === 'number' ? raw.captureSharpness : null,
        attempts: typeof raw.attempts === 'number' ? raw.attempts : 0,
        lastError: String(raw.lastError || ''),
    };
}

async function readQueue(): Promise<EcgQueueItem[]> {
    try {
        const stored = (await idbGet<StoredEcgQueueItem[]>(queueKey())) || [];
        return stored.map(normalizeItem);
    } catch (e) {
        log.warn('queue read failed', e);
        return [];
    }
}

async function writeQueue(items: EcgQueueItem[]): Promise<void> {
    await idbSet(queueKey(), items);
    emit();
}

/** Отложенные разборы текущей базы (для панели и счётчика). */
export async function listEcgQueue(): Promise<EcgQueueItem[]> {
    return readQueue();
}

/** Элементы, ожидающие разбора (снимок без результата). */
export async function listEcgCapturedQueue(): Promise<EcgQueueItem[]> {
    return (await readQueue()).filter((item) => item.stage === 'captured');
}

/**
 * queueEcgCapture — сохранить СНИМОК без связи (durable IndexedDB).
 * Разбор выполнится автоматически при восстановлении связи; снимок при этом
 * не покидает устройство и не теряется при перезапуске приложения.
 */
export async function queueEcgCapture(input: QueueEcgCaptureInput): Promise<EcgQueueItem> {
    if (!input.ownerId) throw new Error('Не удалось определить текущего пользователя');
    const item: EcgQueueItem = {
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        ownerId: input.ownerId,
        fileName: input.file.name || 'ecg.jpg',
        fileType: input.file.type || 'image/jpeg',
        fileBytes: await input.file.arrayBuffer(),
        stage: 'captured',
        state: 'queued',
        studyStatus: 'processed',
        qualityLine: '',
        reason: input.reason || '',
        pipelineVersion: '',
        measurements: {
            hrBpm: null,
            prMs: null,
            qrsMs: null,
            qtMs: null,
            qtcMs: null,
            axisDeg: null,
        },
        findings: [],
        fileId: '',
        captureSharpness: typeof input.sharpnessScore === 'number' ? input.sharpnessScore : null,
        attempts: 0,
        lastError: '',
    };
    const items = await readQueue();
    items.push(item);
    await writeQueue(items);
    log.info('capture queued', { id: item.id, fileName: item.fileName });
    return item;
}

async function enqueue(input: SaveEcgStudyInput, fileId: string): Promise<SaveEcgStudyResult> {
    const item: EcgQueueItem = {
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        ownerId: input.ownerId,
        fileName: input.file.name,
        fileType: input.file.type || 'image/jpeg',
        fileBytes: await input.file.arrayBuffer(),
        stage: 'analyzed',
        state: 'queued',
        studyStatus: input.status,
        qualityLine: input.qualityLine,
        reason: input.reason,
        pipelineVersion: input.pipelineVersion,
        measurements: input.measurements,
        findings: input.findings,
        fileId,
        captureSharpness: null,
        attempts: 0,
        lastError: '',
    };
    const items = await readQueue();
    items.push(item);
    await writeQueue(items);
    log.info('study queued', { id: item.id, fileName: item.fileName });
    return {
        studyId: '',
        fileId,
        findingsSaved: 0,
        findingsFailed: [],
        queued: true,
        queueId: item.id,
    };
}

/**
 * saveEcgStudy — офлайн-first сохранение разбора.
 * Онлайн — сразу на сервер; офлайн или транспортный сбой — durable-очередь.
 * Ошибки данных/прав пробрасываются (повтор бессмысленен).
 */
export async function saveEcgStudy(input: SaveEcgStudyInput): Promise<SaveEcgStudyResult> {
    if (!input.ownerId) throw new Error('Не удалось определить текущего пользователя');
    if (isEffectivelyOffline()) return enqueue(input, '');

    let uploadedFileId = '';
    try {
        return await persistEcgStudy(input, {
            onFileUploaded: (fileId) => {
                uploadedFileId = fileId;
            },
        });
    } catch (e) {
        if (e instanceof EcgSaveOfflineError || isTransportError(e)) {
            return enqueue(input, uploadedFileId);
        }
        throw e;
    }
}

async function updateItem(id: string, patch: Partial<EcgQueueItem>): Promise<void> {
    const items = await readQueue();
    const next = items.map((item) => (item.id === id ? { ...item, ...patch } : item));
    await writeQueue(next);
}

async function removeItem(id: string): Promise<void> {
    const items = await readQueue();
    await writeQueue(items.filter((item) => item.id !== id));
}

/** id, отменённые во время отправки: завершаем текущий запрос и не сохраняем. */
const cancelled = new Set<string>();
/** Элементы, по которым прямо сейчас идёт сетевой шаг (для отмены). */
const activeSending = new Set<string>();

/**
 * Отменить отложенный разбор (снимок и результат удаляются с устройства).
 * Если элемент уже отправляется — отмена вступает в силу после текущего
 * запроса (разбор не сохраняется).
 */
export async function cancelEcgQueueItem(id: string): Promise<void> {
    // Отмена посреди отправки действует после текущего сетевого шага.
    if (activeSending.has(id)) cancelled.add(id);
    else cancelled.delete(id);
    clearProgress(id);
    await removeItem(id);
}

/** Совместимость: раньше элемент «удалялся» — теперь это та же отмена. */
export async function removeEcgQueueItem(id: string): Promise<void> {
    await cancelEcgQueueItem(id);
}

/** Повторить ошибочный элемент (снять отметку ошибки и отправить снова). */
export async function retryEcgQueueItem(id: string): Promise<void> {
    cancelled.delete(id);
    await updateItem(id, { state: 'queued', lastError: '' });
    await flushEcgStudyQueue();
}

let flushing: Promise<EcgQueueFlushResult> | null = null;

/**
 * flushEcgStudyQueue — отправить отложенные снимки/разборы.
 * Последовательно (не плодим параллельные загрузки), single-flight.
 * Транспортный сбой — останавливаемся (связи нет) и сохраняем текст ошибки;
 * серверная ошибка — помечаем элемент, идём дальше.
 */
export function flushEcgStudyQueue(): Promise<EcgQueueFlushResult> {
    if (!flushing) {
        flushing = doFlush().finally(() => {
            flushing = null;
        });
    }
    return flushing;
}

function fileOf(item: EcgQueueItem): File {
    return new File([item.fileBytes], item.fileName, {
        type: item.fileType || 'image/jpeg',
    });
}

/**
 * analyzeCaptured — разобрать снимок на сервере и зафиксировать результат в
 * элементе очереди (stage='analyzed'), чтобы повтор при сбое сохранения не
 * отправлял снимок на разбор заново.
 */
async function analyzeCaptured(item: EcgQueueItem): Promise<EcgQueueItem> {
    setProgress(item.id, 'analyzing', null);
    const analysis = await analyzeEcgFile(fileOf(item));
    if (cancelled.has(item.id)) return item;
    if (analysis.status !== 'ok' && analysis.status !== 'retake') {
        throw new Error(analysis.reason || 'Сервис не смог разобрать снимок');
    }
    const updated: EcgQueueItem = {
        ...item,
        stage: 'analyzed',
        state: 'sending',
        studyStatus: analysis.status === 'ok' ? 'processed' : 'retake',
        qualityLine: buildQualityLine(
            analysis.quality.verdict,
            item.captureSharpness,
            analysis.quality.leadsExtracted,
        ),
        reason: analysis.reason,
        pipelineVersion: analysis.pipelineVersion,
        measurements: analysis.measurements,
        findings: analysis.findings.map((finding) => ({
            code: finding.code,
            probability: finding.probability,
        })),
    };
    await updateItem(item.id, updated);
    return updated;
}

async function doFlush(): Promise<EcgQueueFlushResult> {
    const items = await readQueue();
    if (items.length === 0) return { saved: 0, failed: 0, pending: 0, savedIds: [] };
    if (isEffectivelyOffline()) return { saved: 0, failed: 0, pending: items.length, savedIds: [] };
    // Без входа отправлять нечего (на лаунчере очередь ждёт, а не «падает»).
    if (!useCenterStore.getState().token) {
        return { saved: 0, failed: 0, pending: items.length, savedIds: [] };
    }

    let saved = 0;
    let failed = 0;
    const savedIds: string[] = [];
    for (let index = 0; index < items.length; index++) {
        const item = items[index];
        if (cancelled.has(item.id)) {
            await removeItem(item.id);
            cancelled.delete(item.id);
            continue;
        }
        const startedAt = Date.now();
        activeSending.add(item.id);
        await updateItem(item.id, { state: 'sending', lastError: '' });
        try {
            let current = item;
            if (current.stage === 'captured') {
                current = await analyzeCaptured(current);
                if (cancelled.has(current.id)) {
                    await removeItem(current.id);
                    cancelled.delete(current.id);
                    continue;
                }
            }

            // Снимок уже загружен (повтор после сбоя) — сразу сохранение.
            if (current.fileId) setProgress(current.id, 'saving', null);
            else setProgress(current.id, 'uploading', 0);

            const result = await persistEcgStudy(
                {
                    ownerId: current.ownerId,
                    file: fileOf(current),
                    status: current.studyStatus,
                    qualityLine: current.qualityLine,
                    reason: current.reason,
                    pipelineVersion: current.pipelineVersion,
                    measurements: current.measurements,
                    findings: current.findings,
                    studyName: formatStudyName(new Date(current.createdAt)),
                },
                {
                    fileId: current.fileId || undefined,
                    onFileUploaded: (fileId) => {
                        current = { ...current, fileId };
                        setProgress(current.id, 'saving', null);
                    },
                    onFileProgress: (fraction) => setProgress(current.id, 'uploading', fraction),
                },
            );
            if (result.findingsFailed.length > 0) {
                for (const failure of result.findingsFailed) {
                    logErrorToJournal(failure.error, `ЭКГ-разбор: находка «${failure.code}» из очереди`);
                }
            }
            if (cancelled.has(item.id)) {
                // Пока шло сохранение, человек отменил элемент — запись уже
                // создана на сервере, но очередь очищаем без «успеха».
                await removeItem(item.id);
                cancelled.delete(item.id);
                continue;
            }
            await removeItem(item.id);
            clearProgress(item.id);
            saved++;
            savedIds.push(item.id);
            log.info('queued item sent', {
                id: item.id,
                stage: current.stage,
                studyId: result.studyId,
                ms: Date.now() - startedAt,
            });
        } catch (e) {
            const message = friendlyError(e);
            const transport = e instanceof EcgSaveOfflineError || isTransportError(e);
            await updateItem(item.id, {
                attempts: item.attempts + 1,
                lastError: message,
                state: transport ? 'queued' : 'error',
            });
            clearProgress(item.id);
            if (cancelled.has(item.id)) {
                await removeItem(item.id);
                cancelled.delete(item.id);
                continue;
            }
            if (transport) {
                // Связи нет: не крутим остальные элементы вхолостую.
                log.warn('flush stopped: offline', { id: item.id, error: message });
                const pending = items.length - index;
                emit();
                return { saved, failed, pending, savedIds };
            }
            failed++;
            logErrorToJournal(e, 'ЭКГ-разбор: отправка отложенного элемента');
        } finally {
            activeSending.delete(item.id);
        }
    }
    const pending = (await readQueue()).length;
    emit();
    return { saved, failed, pending, savedIds };
}
